import { Swipe } from '@mui/icons-material';
import React, { useEffect, useRef, useState } from 'react';

import { EStylings } from '../enums/EStylings';
import HGeneric from '../helpers/HGeneric';
import { updateStateAttribute } from '../helpers/helper';
import { useAppSelector } from '../hooks/hooks';
import './CHorizontalScrollContainer.css';
import CLocalizedText from './CLocalizedText';
import CLocalizedTextString from './CLocalizedTextString';

interface Props {
  children: React.ReactNode;
  sidePadding?: React.ReactNode;
  extendTouchscreenScrollArea?: boolean;
  extendedScrollAreaBoundary?: boolean;
  hideHelper?: boolean;
  hideScroller?: boolean;
}

interface State {
  ratio: number;
  isOverflown?: boolean;
  isDragging?: boolean;
  scrollbarOffsetX?: number;
}

export default function CHorizontalScrollContainer(props: Props) {
  const [state, setState] = useState<State>({ ratio: 1 });
  const { isTouchscreen, dashboardOutletDimensions } = useAppSelector((s) => s.dashboard);

  const scrollBoxRef = useRef<HTMLDivElement>(null);
  const sidePaddingRef = useRef<HTMLDivElement>(null);
  const scrollContent = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const dragMetaRef = useRef<{ pointerId: number | null; offsetWithinThumb: number; trackRect: DOMRect | null }>({
    pointerId: null,
    offsetWithinThumb: 0,
    trackRect: null
  });

  const swipeToScrollMessage = CLocalizedTextString('messageSwipeToScroll');

  const getContainerWidth = () => scrollBoxRef.current?.clientWidth ?? dashboardOutletDimensions.w;

  const computePaddingWidth = () => {
    const containerW = getContainerWidth();
    return (
      (Math.max(containerW, EStylings.MAX_PAGE_MODULE_WIDTH) - EStylings.MAX_PAGE_MODULE_WIDTH) / 2 +
      (document.documentElement.clientWidth >= 640 ? 4 : 1) * HGeneric.getRemInPx()
    );
  };

  const computeColumnWidth = (): number => {
    const containerW = getContainerWidth();
    return containerW - 2 * computePaddingWidth();
  };

  const sidePadding = (
    <>{props.sidePadding ?? <div className="flex-shrink-0" style={{ width: computePaddingWidth() }} />}</>
  );

  const checkIfOverflown = () => {
    const scrollBoxDOM = scrollBoxRef.current;
    if (!scrollBoxDOM) return;
    updateStateAttribute<State>({ isOverflown: scrollBoxDOM.scrollWidth > scrollBoxDOM.clientWidth }, setState);
  };

  const computeSidePaddingWidth = () => sidePaddingRef.current?.clientWidth ?? 1;

  const computeContentScrollWidth = () => {
    return (scrollContent.current?.getBoundingClientRect().width ?? 1) - 2 * computeSidePaddingWidth();
  };

  const computeScrollbarWidth = () => {
    const visible = getContainerWidth() - 2 * computeSidePaddingWidth();
    return Math.max(0, visible);
  };

  useEffect(() => {
    const content = computeContentScrollWidth();
    const bar = computeScrollbarWidth();
    // if (!isFinite(content) || !isFinite(bar) || content <= 0 || bar <= 0) return 0;
    updateStateAttribute<State>({ ratio: bar / content }, setState);
  }, [dashboardOutletDimensions.w, props.children]);

  const computeScrollbarSliderWidth = () => {
    const w = computeScrollbarWidth();
    return Math.max(24, state.ratio * w);
  };

  const handleSelectStart = (ev: Event) => {
    if (state.isDragging) ev.preventDefault();
  };

  const startDragging = (pointerId: number, offsetWithinThumb: number, trackRect: DOMRect) => {
    dragMetaRef.current.pointerId = pointerId;
    dragMetaRef.current.offsetWithinThumb = offsetWithinThumb;
    dragMetaRef.current.trackRect = trackRect;
    updateStateAttribute<State>({ isDragging: true }, setState);
    document.body.style.cursor = 'grabbing';
  };

  const stopDragging = () => {
    dragMetaRef.current.pointerId = null;
    dragMetaRef.current.trackRect = null;
    updateStateAttribute<State>({ isDragging: false }, setState);
    document.body.style.cursor = 'auto';
  };

  const applyThumbFromClientX = (clientX: number) => {
    const trackRect = dragMetaRef.current.trackRect ?? trackRef.current?.getBoundingClientRect();
    if (!trackRect) return;

    const thumbW = computeScrollbarSliderWidth();
    const trackW = computeScrollbarWidth();

    const raw = clientX - trackRect.left - dragMetaRef.current.offsetWithinThumb;
    const newOffset = Math.min(Math.max(raw, 0), trackW - thumbW);

    scrollBoxRef.current?.scroll({ top: 0, left: state.ratio > 0 ? newOffset / state.ratio : 0 });
    if (state.scrollbarOffsetX !== newOffset) {
      updateStateAttribute<State>({ scrollbarOffsetX: newOffset }, setState);
    }
  };

  const handleTrackPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    const track = e.currentTarget;
    track.setPointerCapture(e.pointerId);

    const rect = track.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const currentOffset = state.scrollbarOffsetX ?? 0;
    const thumbW = computeScrollbarSliderWidth();
    const insideThumb = clickX >= currentOffset && clickX <= currentOffset + thumbW;

    if (!insideThumb) {
      const targetOffset = Math.min(Math.max(clickX - thumbW / 2, 0), computeScrollbarWidth() - thumbW);
      scrollBoxRef.current?.scroll({ top: 0, left: state.ratio > 0 ? targetOffset / state.ratio : 0 });
      if (state.scrollbarOffsetX !== targetOffset) {
        updateStateAttribute<State>({ scrollbarOffsetX: targetOffset }, setState);
      }
      startDragging(e.pointerId, thumbW / 2, rect);
      return;
    }

    const offsetWithinThumb = clickX - currentOffset;
    startDragging(e.pointerId, offsetWithinThumb, rect);
  };

  const handleThumbPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    const track = trackRef.current;
    if (!track) return;
    track.setPointerCapture(e.pointerId);

    const rect = track.getBoundingClientRect();
    const currentOffset = state.scrollbarOffsetX ?? 0;
    const clickX = e.clientX - rect.left;
    const offsetWithinThumb = clickX - currentOffset;

    startDragging(e.pointerId, offsetWithinThumb, rect);
  };

  const handleWindowPointerMove = (e: PointerEvent) => {
    if (!state.isDragging) return;
    if (dragMetaRef.current.pointerId !== e.pointerId) return;
    e.preventDefault();
    applyThumbFromClientX(e.clientX);
  };

  const handleWindowPointerUp = (e: PointerEvent) => {
    if (!state.isDragging) return;
    if (dragMetaRef.current.pointerId !== e.pointerId) return;
    stopDragging();
  };

  const updateSliderPosition = (deltaX: number) => {
    // Browser will scroll the element on wheel. We only mirror to thumb.
    const el = scrollBoxRef.current;
    if (!el) return;
    const predicted = Math.min(
      computeContentScrollWidth() - computeScrollbarWidth(),
      Math.max(0, el.scrollLeft + deltaX)
    );
    const newOffset = predicted * state.ratio;
    if (state.scrollbarOffsetX !== newOffset) {
      updateStateAttribute<State>({ scrollbarOffsetX: newOffset }, setState);
    }
  };

  const handleWheel = (ev: React.WheelEvent) => {
    if (!ev.deltaX) return;
    updateSliderPosition(ev.deltaX);
  };

  const syncThumbFromScroll = () => {
    const left = scrollBoxRef.current?.scrollLeft ?? 0;
    const newOffset = left * state.ratio;
    if (state.scrollbarOffsetX === newOffset) return;
    updateStateAttribute<State>({ scrollbarOffsetX: newOffset }, setState);
  };

  useEffect(() => {
    checkIfOverflown();
    // seed thumb from current scroll
    syncThumbFromScroll();
  }, [dashboardOutletDimensions.w, props.children]);

  useEffect(() => {
    const ro = new ResizeObserver(() => {
      checkIfOverflown();
      syncThumbFromScroll();
    });
    if (scrollBoxRef.current) ro.observe(scrollBoxRef.current);
    return () => {
      ro.disconnect();
    };
  }, []);

  // Global pointer listeners, depend only on dragging flag
  useEffect(() => {
    window.addEventListener('pointermove', handleWindowPointerMove, { passive: false });
    window.addEventListener('pointerup', handleWindowPointerUp, { passive: false });
    window.addEventListener('selectstart', handleSelectStart);
    return () => {
      window.removeEventListener('pointermove', handleWindowPointerMove);
      window.removeEventListener('pointerup', handleWindowPointerUp);
      window.removeEventListener('selectstart', handleSelectStart);
    };
  }, [state.isDragging]);

  // Keep thumb in sync with native swipe and momentum on the element
  useEffect(() => {
    const el = scrollBoxRef.current;
    if (!el) return;

    let raf = 0;
    const onScroll = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(syncThumbFromScroll);
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    syncThumbFromScroll();

    return () => {
      el.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [state.ratio]);

  return (
    <div className={'relative flex flex-col gap-4'}>
      <div
        className={'overflow-x-scroll c-page-module-plot snap-x snap-mandatory'}
        ref={scrollBoxRef}
        onWheel={handleWheel}
      >
        <div className={'relative flex gap-0 w-fit'} ref={scrollContent}>
          <div ref={sidePaddingRef}>{sidePadding}</div>
          <div style={{ minWidth: computeColumnWidth() }}>{props.children}</div>
          {sidePadding}
        </div>
        {isTouchscreen && state.isOverflown && props.extendTouchscreenScrollArea && !props.hideHelper && (
          <div className={'h-16'}>
            <span
              className={
                'text-xs opacity-60 absolute left-[50%] bottom-0 pointer-events-none text-center w-[90%] translate-x-[-50%] translate-y-[-2rem]'
              }
            >
              <Swipe className={'inline'} />
              <span>&nbsp;&nbsp;{swipeToScrollMessage}</span>
            </span>
          </div>
        )}
      </div>

      {state.isOverflown && !props.hideScroller && (
        <div
          className={'relative flex flex-col gap-3'}
          style={{
            width: computeScrollbarWidth(),
            marginLeft: computeSidePaddingWidth(),
            marginRight: computeSidePaddingWidth()
          }}
        >
          <div
            ref={trackRef}
            className={'relative w-full h-[2px] rounded-full bg-black bg-opacity-5 select-none touch-none'}
            onPointerDown={handleTrackPointerDown}
          >
            <div
              className={
                'absolute top-0 left-0 h-[2px] rounded-full bg-black bg-opacity-20 hover:bg-opacity-25 active:bg-opacity-30 select-none touch-none'
              }
              style={{
                width: computeScrollbarSliderWidth(),
                transform: `translateX(${state.scrollbarOffsetX ?? 0}px)`,
                cursor: 'grab'
              }}
              onPointerDown={handleThumbPointerDown}
            />
          </div>
          {!isTouchscreen && !props.hideHelper && (
            <div className={'flex flex-wrap justify-center text-center text-xs opacity-60'}>
              <CLocalizedText dictKey={'messageHoldShiftToScroll1'} inline />
              &nbsp;
              <span className={'py-0 px-1 border border-gray-500 rounded-md'}>&nbsp;Shift &#8679;</span>
              &nbsp;&nbsp;
              <CLocalizedText dictKey={'messageHoldShiftToScroll2'} inline />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
